import { FileSpreadsheet, FileText, FileJson, Disc } from 'lucide-react';
import toast from 'react-hot-toast';
import type { SongRequest } from '../../services/requests';
import type { EventAnalytics } from '../../types/analytics';
import type { Event } from '../../services/events';
import { generateCSV, downloadFile, formatDuration } from '../../utils/analyticsUtils';

interface Props {
  requests: SongRequest[];
  analytics: EventAnalytics | null;
  event: Event | null;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
}

const STATUS_FILTERS = [
  { value: 'all', label: 'All Requests' },
  { value: 'queued', label: 'Queued' },
  { value: 'played', label: 'Played' },
  { value: 'pending', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
];

export default function AnalyticsExport({
  requests, analytics, event, statusFilter, onStatusFilterChange,
}: Props) {
  const filteredRequests = statusFilter === 'all'
    ? requests
    : requests.filter(r => r.status === statusFilter);

  const handleExportCSV = () => {
    if (filteredRequests.length === 0) {
      toast.error('No data to export');
      return;
    }
    const eventName = event?.name || 'Event';
    const filterSuffix = statusFilter !== 'all' ? `_${statusFilter}` : '';
    const csv = generateCSV(filteredRequests, eventName);
    const filename = `${eventName.replace(/[^a-z0-9]/gi, '_')}_queue${filterSuffix}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadFile(csv, filename, 'text/csv;charset=utf-8;');
    toast.success(`Exported ${filteredRequests.length} requests to CSV`);
  };

  const handleExportJSON = () => {
    if (!analytics) {
      toast.error('No analytics data to export');
      return;
    }
    const json = JSON.stringify(analytics, null, 2);
    const filename = `${(event?.name || 'Event').replace(/[^a-z0-9]/gi, '_')}_analytics_${new Date().toISOString().split('T')[0]}.json`;
    downloadFile(json, filename, 'application/json;charset=utf-8;');
    toast.success('Analytics exported as JSON');
  };

  const handleExportSummary = () => {
    if (requests.length === 0) {
      toast.error('No data to export');
      return;
    }
    const eventName = event?.name || 'Event';
    let content = `${eventName}\n`;
    content += `Date: ${event?.date || ''}\n`;
    content += `Time: ${event?.startTime || ''} - ${event?.endTime || ''}\n`;
    content += `Generated: ${new Date().toLocaleString()}\n`;
    content += `\n${'='.repeat(60)}\n`;
    content += `\nSTATISTICS\n`;
    content += `-`.repeat(30) + '\n';
    content += `Total Requests: ${requests.length}\n`;
    content += `Total Votes: ${requests.reduce((sum, r) => sum + r.voteCount, 0)}\n`;
    content += `Unique Attendees: ${new Set(requests.map(r => r.requestedBy.oderId)).size}\n`;
    if (analytics) {
      content += `Engagement Rate: ${analytics.summary.engagementRate}%\n`;
      content += `Request Success Rate: ${analytics.queue.requestSuccessRate}%\n`;
    }
    content += `\n${'='.repeat(60)}\n`;
    content += `\nQUEUE LIST\n`;
    content += `-`.repeat(30) + '\n\n';

    [...requests]
      .sort((a, b) => b.voteCount - a.voteCount)
      .forEach((r, i) => {
        content += `${i + 1}. ${r.song.title} - ${r.song.artist}\n`;
        content += `   Status: ${r.status} | Votes: ${r.voteCount} | Duration: ${formatDuration(r.song.durationMs)}\n`;
        if (r.requestedBy.nickname) content += `   Requested by: ${r.requestedBy.nickname}\n`;
        if (r.message) content += `   Message: "${r.message}"\n`;
        content += '\n';
      });

    const filename = `${eventName.replace(/[^a-z0-9]/gi, '_')}_summary_${new Date().toISOString().split('T')[0]}.txt`;
    downloadFile(content, filename, 'text/plain;charset=utf-8;');
    toast.success('Summary exported successfully!');
  };

  const handleExportSpotifyReport = () => {
    if (!analytics || requests.length === 0) {
      toast.error('No data to export');
      return;
    }
    const eventName = event?.name || 'Event';
    const d = analytics.discovery;
    const s = analytics.summary;

    let report = `VOTEBEATS SPOTIFY ENGAGEMENT REPORT\n`;
    report += `${'='.repeat(50)}\n\n`;
    report += `Event: ${eventName}\n`;
    report += `Date: ${event?.date || ''}\n`;
    report += `Generated: ${new Date().toLocaleString()}\n\n`;

    report += `AUDIENCE ENGAGEMENT\n`;
    report += `-`.repeat(40) + '\n';
    report += `Total Active Participants: ${s.uniqueAttendees}\n`;
    report += `Song Requests Submitted: ${s.totalRequests}\n`;
    report += `Total Votes Cast: ${s.totalVotes}\n`;
    report += `Engagement Rate: ${s.engagementRate}%\n`;
    report += `Avg Votes per Voter: ${s.avgVotesPerAttendee}\n\n`;

    report += `SPOTIFY STREAMING IMPACT\n`;
    report += `-`.repeat(40) + '\n';
    report += `Estimated Streams Driven: ${d.estimatedStreams}\n`;
    report += `Songs with Spotify URI: ${d.songsWithSpotifyUri} / ${s.totalRequests}\n`;
    report += `Spotify Coverage: ${s.totalRequests > 0 ? Math.round((d.songsWithSpotifyUri / s.totalRequests) * 100) : 0}%\n\n`;

    report += `SONG DISCOVERY\n`;
    report += `-`.repeat(40) + '\n';
    report += `Discovery Rate: ${d.discoveryRate}% (unique songs vs requests)\n`;
    report += `Cross-Genre Voters: ${d.crossGenreEngagement} (voted on 3+ genres)\n`;
    report += `Genre Diversity: ${Object.keys(d.genreDistribution).length} genres\n`;
    report += `Top Genre: ${d.topGenre}\n\n`;

    report += `GENRE BREAKDOWN\n`;
    report += `-`.repeat(40) + '\n';
    Object.entries(d.genreDistribution)
      .sort(([, a], [, b]) => b - a)
      .forEach(([genre, count]) => {
        const pct = s.totalRequests > 0 ? Math.round((count / s.totalRequests) * 100) : 0;
        report += `  ${genre}: ${count} songs (${pct}%)\n`;
      });

    report += `\n${'='.repeat(50)}\n`;
    report += `Report generated by VoteBeats (votebeatsv3-39844.web.app)\n`;

    const filename = `${eventName.replace(/[^a-z0-9]/gi, '_')}_spotify_report_${new Date().toISOString().split('T')[0]}.txt`;
    downloadFile(report, filename, 'text/plain;charset=utf-8;');
    toast.success('Spotify report exported!');
  };

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <select
        value={statusFilter}
        onChange={(e) => onStatusFilterChange(e.target.value)}
        className="input text-sm"
        aria-label="Filter by status for export"
      >
        {STATUS_FILTERS.map((filter) => (
          <option key={filter.value} value={filter.value}>
            {filter.label}
          </option>
        ))}
      </select>
      <button onClick={handleExportCSV} className="btn btn-secondary text-sm">
        <FileSpreadsheet className="w-4 h-4 mr-2" />
        CSV ({filteredRequests.length})
      </button>
      <button onClick={handleExportSummary} className="btn btn-secondary text-sm">
        <FileText className="w-4 h-4 mr-2" />
        Summary
      </button>
      <button onClick={handleExportJSON} className="btn btn-secondary text-sm">
        <FileJson className="w-4 h-4 mr-2" />
        JSON
      </button>
      {/* Hidden: Spotify API integration paused */}
      {false && (
      <button onClick={handleExportSpotifyReport} className="btn btn-secondary text-sm">
        <Disc className="w-4 h-4 mr-2" />
        Spotify Report
      </button>
      )}
    </div>
  );
}
